import { Injectable, OnDestroy, signal } from "@angular/core";

@Injectable({ providedIn: "root" })
export class MobileNetworkService implements OnDestroy {
  private listener?: { remove: () => Promise<void> } | null;
  private readonly onlineSignal = signal<boolean>(navigator.onLine);

  readonly isOnline = this.onlineSignal.asReadonly();

  constructor() {
    this.initialize().catch(() => undefined);
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
  }

  ngOnDestroy(): void {
    this.listener?.remove().catch(() => undefined);
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
  }

  private async initialize(): Promise<void> {
    try {
      const networkPlugin = (
        window as Window & {
          Capacitor?: {
            Plugins?: {
              Network?: {
                getStatus?: () => Promise<{ connected: boolean }>;
                addListener?: (
                  eventName: "networkStatusChange",
                  listener: (status: { connected: boolean }) => void
                ) => Promise<{ remove: () => Promise<void> }>;
              };
            };
          };
        }
      ).Capacitor?.Plugins?.Network;

      if (networkPlugin?.getStatus) {
        const status = await networkPlugin.getStatus();
        this.onlineSignal.set(status.connected);
      }

      if (networkPlugin?.addListener) {
        this.listener = await networkPlugin.addListener("networkStatusChange", (nextStatus) => {
          this.onlineSignal.set(nextStatus.connected);
        });
      }
    } catch {
      this.onlineSignal.set(navigator.onLine);
    }
  }

  private readonly handleOnline = () => {
    this.onlineSignal.set(true);
  };

  private readonly handleOffline = () => {
    this.onlineSignal.set(false);
  };
}
